import { keyframes } from '@emotion/react';
import theme from '@styles/theme';
import reusables from '@styles/theme/reusables';

const { fadeIn, fadeOut } = reusables.animations;

const OpenAnimation = keyframes({
  '0%': {
    height: '0',
    overflowY: 'hidden',
  },
  '99%': {
    overflowY: 'hidden',
  },
  '100%': {
    height: 'calc(100vh - 100px)',
    overflowY: 'auto',
  },
});
const CloseAnimation = keyframes({
  '0%': {
    height: 'calc(100vh - 100px)',
    overflowY: 'hidden',
  },
  '100%': {
    height: '0',
    overflowY: 'hidden',
  },
});

const hoverElement = (active = true) => ({
  content: '""',
  position: 'absolute',
  width: '100%',
  opacity: active ? 1 : 0,
  left: 0,
  bottom: '-4px',
  height: '1px',
  background: theme.rawColors.darkGrey700,
});

export default {
  megaMenuContainer: (openAnimation: boolean, isMenuVisible: boolean) => ({
    display: isMenuVisible ? ['none', 'none', 'none', 'none', 'flex'] : 'none',
    width: '100%',
    height: '0',
    margin: 'auto',
    background: theme.background.megaNav,
    overflowX: 'hidden',
    overflowY: 'hidden',
    animation: openAnimation ? `${OpenAnimation} .45s both` : `${CloseAnimation} .45s both`,
    animationTimingFunction: 'cubic-bezier(.54, 0, .58, 1)',
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100px',
    zIndex: theme.zIndex.megaMenu,
    'a:hover, a:visited': {
      color: theme.rawColors.neutralBlack,
      textDecoration: 'none',
    },
    ...reusables.scrollbar(),
  }),
  megaMenuContainerInner: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: '0 auto',
    height: 'calc(100vh - 100px)',
    position: 'relative',
  },
  megaMenuContentContainer: {
    display: 'flex',
    height: 'auto',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'strech',
    margin: '0 auto',
    padding: ['0', '0', '0', '0', '40px', '60px 60px 40px 60px', '60px 60px 40px 60px'],
    '&.user-panel': {
      background: theme.rawColors.neutralWhite,
      mt: '1px',
    },
  },
  megaMenuUserPanelContainer: (isFlex = true) => ({
    display: isFlex ? 'flex' : 'block',
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    margin: '0 auto',
    padding: ['0', '0', '0', '0', '40px', '60px', '80px'],
    paddingRight: ['0', '0', '0', '0', '1px', '1px', '1px', '1px'],
    paddingBottom: ['0', '0', '0', '0', '0', '0', '0'],
    '&.user-panel': {
      background: theme.rawColors.neutralWhite,
      mt: '1px',
      overflowY: 'auto',
      pl: '0',
    },
  }),
  marketingProductsContainer: {
    marginRight: '24px',
  },
  menuMarketingProduct: {
    height: '100%',
  },
  marketingProducts: (isVisible: boolean) => ({
    height: '100%',
    minHeight: [
      'unset',
      'unset',
      'unset',
      'unset',
      'calc(100vh - 180px)',
      'calc(100vh - 220px)',
      'calc(100vh - 260px)',
    ],
    maxWidth: '342px',
    padding: ['0', '0', '0', '0', '24px', '32px', '40px'],

    display: isVisible ? 'flex' : 'none',
    flexDirection: 'column',
    background: theme.rawColors.neutralWhite,
  }),
  marketingProductsType: {
    listStyle: 'none',
    marginBottom: '40px',
  },
  marketingProductsTypeCollection: {
    listStyle: 'none',
  },
  marketingProductsItemType: {
    marginBottom: '20px',

    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  marketingProductsItem: (isTooLong = false, haveLabel = false) => ({
    marginBottom: '20px',
    display: haveLabel ? 'flex' : 'block',
    flexGrow: 1,
    position: 'relative',
    fontSize: theme.fontSizes.h4,
    '&:last-of-type': {
      marginBottom: 0,
    },
    'button, a': {
      fontFamily: theme.fonts.uniform,
      fontWeight: theme.fontWeights.normal,
      fontSize: '20px',
      lineHeight: '28px',
      letterSpacing: theme.letterSpacings.caps,
      color: theme.rawColors.darkGrey700,
      textAlign: 'left',
      display: 'flex',
      '&:hover': {
        '& > span > span > span:last-child': {
          '&:before': hoverElement(),
        },
      },
      '& > span > span ': {
        display: haveLabel || isTooLong ? 'flex' : 'block',
        flexFlow: 'row wrap',
        position: 'relative',
        maxWidth: 300,
        width: isTooLong && haveLabel ? 'min-content' : 'unset',
        span: {
          position: 'relative',
          width: 'auto',
        },
      },
    },
  }),
  productActive: (isActive: boolean) => ({
    cursor: 'pointer',
    position: 'relative',
    '&:before': hoverElement(isActive),

    '&:hover': {
      '&:before': hoverElement(),
    },
  }),
  marketingTopButton: {
    marginBottom: '40px',
    button: {
      height: '32px',
    },
  },
  productsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: ['0', '0', '0', '0', '24px', '32px', '40px'],
    height: '100%',
    background: theme.rawColors.neutralWhite,

    overflow: 'hidden',
    overflowY: 'auto',

    ...reusables.scrollbar(),
  },
  productsContentContainer: {
    display: 'flex',
    flexShrink: 0,
    flexGrow: 1,
    height: 'auto',
  },
  products: (withPaddingTop: boolean) => ({
    listStyle: 'none',
    paddingRight: ['0', '0', '0', '0', '24px', '32px', '40px', '80px'],
    paddingTop: withPaddingTop ? ['0', '0', '0', '0', '48px'] : 0,

    '&:last-of-type': {
      paddingRight: ['0', '0', '0', '0', '0', '0', '0', '40px'],
    },
  }),
  productsHeading: {
    pb: 20,
    fontSize: theme.fontSizes.h4,
    fontFamily: [
      theme.fonts.uniformMedium,
      theme.fonts.uniformMedium,
      theme.fonts.uniformMedium,
      theme.fonts.uniformMedium,
      theme.fonts.uniform,
    ],
    letterSpacing: theme.letterSpacings.caps,
  },
  productsLast: {
    listStyle: 'none',
    paddingBottom: '24px',
  },
  productItemContainer: (isSmallLink = false) => ({
    marginBottom: '20px',
    display: isSmallLink ? 'flex' : 'block',
    flexShrink: 0,
    fontFamily: theme.fonts.uniform,
    fontWeight: theme.fontWeights.normal,
    fontSize: theme.fontSizes.text,
    letterSpacing: theme.letterSpacings.caps,
    lineHeight: '22px',
    color: theme.rawColors.darkGrey700,
  }),
  productItemLink: (isTooLong: boolean, haveLabel) => ({
    fontFamily: theme.fonts.uniform,
    fontWeight: theme.fontWeights.normal,
    fontSize: '16px',
    lineHeight: '22px',
    color: theme.rawColors.darkGrey700,
    position: 'relative',
    display: haveLabel ? 'flex' : 'block',
    '& > span': {
      maxWidth: 236,
      width: isTooLong && haveLabel ? 'min-content' : 'unset',
      display: 'block',
    },
    '&:hover': {
      color: theme.rawColors.darkGrey700,
    },
  }),
  imageContainer: {
    height: '100%',
    width: '100%',
    position: 'sticky',
    top: 0,
    bottom: 0,
    right: 0,
  },
  imageWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
  },
  marketingCategoriesContainer: {
    display: 'flex',
    flex: 1,
    background: theme.background.megaNav,
    position: 'relative',
    height: '100%',
  },
  closeButtonContainer: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    height: ['0', '0', '0', '0', '40px', '60px', '80px'],
    position: 'absolute',
    right: ['0', '0', '0', '20px', '20px', '20px'],
    top: ['0', '0', '0', '0', '4px'],
    zIndex: theme.zIndex.megaMenu + 1,
  },
  megaMenuContainerBackdrop: (isVisible: boolean) => ({
    display: isVisible ? ['none', 'none', 'none', 'none', 'block'] : 'none',
    opacity: 0,
    width: '100%',
    height: '100vh',
    backgroundColor: theme.rawColors.neutralBlack,
    animation: isVisible ? `${fadeIn} .45s both` : `${fadeOut} .45s both`,

    position: 'absolute',
    left: 0,
    top: '100px',
    zIndex: theme.zIndex.megaMenu - 1,
  }),

  imageCurrent: (isSelected: boolean, isMarketingProduct: boolean) => ({
    opacity: isSelected ? 1 : 0,
    transition: !isMarketingProduct ? 'opacity .15s ease-in-out' : '',
    objectFit: 'cover',
    objectPosition: 'center',
    height: '100%',
    width: '100%',
  }),
};
